import React from "react";
// Pixel-art icons (pixelarticons, MIT) instead of emoji, for the
// early-2000s look. Each one draws with currentColor.
import { ReactComponent as Home } from "pixelarticons/svg/home.svg";
import { ReactComponent as InboxFull } from "pixelarticons/svg/inbox-full.svg";
import { ReactComponent as Pin } from "pixelarticons/svg/pin.svg";
import { ReactComponent as MailUnread } from "pixelarticons/svg/mail-unread.svg";
import { ReactComponent as Users } from "pixelarticons/svg/users.svg";
import { ReactComponent as Shield } from "pixelarticons/svg/shield.svg";
import { ReactComponent as User } from "pixelarticons/svg/user.svg";
import { ReactComponent as Logout } from "pixelarticons/svg/logout.svg";
import { ReactComponent as Trash } from "pixelarticons/svg/trash.svg";
import { ReactComponent as BookOpen } from "pixelarticons/svg/book-open.svg";
import { ReactComponent as Edit } from "pixelarticons/svg/edit.svg";
import { ReactComponent as Gift } from "pixelarticons/svg/gift.svg";
import { ReactComponent as Mail } from "pixelarticons/svg/mail.svg";
import { ReactComponent as MailArrow } from "pixelarticons/svg/mail-arrow-right.svg";
import { ReactComponent as Heart } from "pixelarticons/svg/heart.svg";
import { ReactComponent as Save } from "pixelarticons/svg/save.svg";
import { ReactComponent as Reply } from "pixelarticons/svg/reply.svg";
import { ReactComponent as Check } from "pixelarticons/svg/check.svg";
import { ReactComponent as Eye } from "pixelarticons/svg/eye.svg";
import { ReactComponent as Warning } from "pixelarticons/svg/warning-box.svg";
import { ReactComponent as Hourglass } from "pixelarticons/svg/hourglass.svg";
import { ReactComponent as Tea } from "pixelarticons/svg/tea.svg";
import { ReactComponent as MoodHappy } from "pixelarticons/svg/mood-happy.svg";
import { ReactComponent as ArrowLeft } from "pixelarticons/svg/arrow-left.svg";
import { ReactComponent as Lock } from "pixelarticons/svg/lock.svg";
import { ReactComponent as Message } from "pixelarticons/svg/message-text.svg";
import { ReactComponent as Notification } from "pixelarticons/svg/notification.svg";
import { ReactComponent as Clock } from "pixelarticons/svg/clock.svg";
import { ReactComponent as MoonStars } from "pixelarticons/svg/moon-stars.svg";
import { ReactComponent as Flag } from "pixelarticons/svg/flag.svg";
import { ReactComponent as Zap } from "pixelarticons/svg/zap.svg";
import { ReactComponent as Close } from "pixelarticons/svg/close.svg";
// avatar choices
import { ReactComponent as Sun } from "pixelarticons/svg/sun.svg";
import { ReactComponent as Cloud } from "pixelarticons/svg/cloud.svg";
import { ReactComponent as Coffee } from "pixelarticons/svg/coffee.svg";
import { ReactComponent as Music } from "pixelarticons/svg/music.svg";
import { ReactComponent as Headphone } from "pixelarticons/svg/headphone.svg";
import { ReactComponent as Gamepad } from "pixelarticons/svg/gamepad.svg";
import { ReactComponent as Dino } from "pixelarticons/svg/downasaur.svg";
import { ReactComponent as Robot } from "pixelarticons/svg/android.svg";
import { ReactComponent as Bug } from "pixelarticons/svg/bug.svg";
import { ReactComponent as Cake } from "pixelarticons/svg/cake.svg";
import { ReactComponent as Art } from "pixelarticons/svg/colors-swatch.svg";
import { ReactComponent as Camera } from "pixelarticons/svg/camera.svg";
import { ReactComponent as Chess } from "pixelarticons/svg/chess.svg";
import { ReactComponent as Waving } from "pixelarticons/svg/human-handsup.svg";
import { ReactComponent as Drop } from "pixelarticons/svg/drop.svg";

// A little pixel spoon (pixelarticons has none), for the spoon meter.
function Spoon(props: React.SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg" {...props}>
      <path
        transform="rotate(35 12 12)"
        d="M10 1h4v1h1v1h1v6h-1v2h-1v1h-1v11h-2V12h-1v-1H9V9H8V3h1V2h1z"
      />
    </svg>
  );
}

const icons = {
  home: Home,
  inbox: InboxFull,
  pin: Pin,
  request: MailUnread,
  community: Users,
  shield: Shield,
  user: User,
  logout: Logout,
  book: BookOpen,
  edit: Edit,
  gift: Gift,
  mail: Mail,
  send: MailArrow,
  heart: Heart,
  save: Save,
  reply: Reply,
  check: Check,
  eye: Eye,
  warning: Warning,
  hourglass: Hourglass,
  tea: Tea,
  happy: MoodHappy,
  back: ArrowLeft,
  lock: Lock,
  message: Message,
  bell: Notification,
  clock: Clock,
  stars: MoonStars,
  flag: Flag,
  zap: Zap,
  close: Close,
  sun: Sun,
  cloud: Cloud,
  coffee: Coffee,
  music: Music,
  headphone: Headphone,
  gamepad: Gamepad,
  dino: Dino,
  robot: Robot,
  bug: Bug,
  cake: Cake,
  art: Art,
  camera: Camera,
  chess: Chess,
  waving: Waving,
  drop: Drop,
  spoon: Spoon,
  trash: Trash,
};

export type IconName = keyof typeof icons;

const tones = {
  pink: "#c94f71",
  blue: "#1f74a3",
  ink: "#4a3426",
  gold: "#b07d00",
  green: "#2f7d4f",
  violet: "#7a4fc9",
  white: "#ffffff",
  current: "currentColor",
};

export type IconTone = keyof typeof tones;

export default function Icon({
  name,
  tone = "current",
  size = 20,
  pop = false,
  className = "",
  label,
}: {
  name: IconName;
  tone?: IconTone;
  size?: number;
  /** adds a hard 3D drop shadow, like a sticker */
  pop?: boolean;
  className?: string;
  /** accessible label; omit for decorative icons */
  label?: string;
}) {
  const Svg = icons[name];
  return (
    <Svg
      width={size}
      height={size}
      className={`pix${pop ? " pix-pop" : ""} ${className}`}
      style={{ color: tones[tone] }}
      role={label ? "img" : undefined}
      aria-label={label}
      aria-hidden={label ? undefined : true}
      focusable="false"
    />
  );
}

// Full class names so Tailwind keeps them (it drops names built at runtime).
const tileTones = {
  pink: "icon-tile-pink",
  blue: "icon-tile-blue",
  gold: "icon-tile-gold",
  green: "icon-tile-green",
  violet: "icon-tile-violet",
};

// An XP-style beveled icon tile, for headings and dashboard cards.
export function IconTile({
  name,
  tone = "pink",
  size = 40,
}: {
  name: IconName;
  tone?: "pink" | "blue" | "gold" | "green" | "violet";
  size?: number;
}) {
  return (
    <span
      className={`icon-tile ${tileTones[tone]}`}
      style={{ width: size, height: size }}
      aria-hidden="true"
    >
      <Icon name={name} tone="white" size={Math.round(size * 0.6)} />
    </span>
  );
}
